import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { usePager, Pager } from "@/components/kiyana/Pager";
import { Layers, Plus, Search, Trash2, Merge, Check, X, Pencil } from "lucide-react";

const inp = "h-9 px-3 rounded-lg bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]";
const PINK = "#C4738A";

export default function AdminCategories() {
  const [cats, setCats] = useState(null);
  const [q, setQ] = useState("");
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");
  const [merging, setMerging] = useState(null);
  const [mergeTarget, setMergeTarget] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/categories").then((r) => setCats(r.data)).catch(() => setCats(false));
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => (cats || []).filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || c.id.includes(q.toLowerCase())), [cats, q]);
  const { paged: pagedCats, pager: catPager } = usePager(filtered);
  const usedCount = (cats || []).filter((c) => c.product_count > 0).length;

  const add = async () => {
    if (newName.trim().length < 2) return toast.error("Nama kategori minimal 2 karakter");
    setBusy(true);
    try {
      await api.post("/admin/categories", { name: newName.trim() });
      toast.success("Kategori ditambahkan");
      setNewName("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah kategori"); }
    finally { setBusy(false); }
  };

  const rename = async (c) => {
    if (editName.trim().length < 2) return toast.error("Nama kategori minimal 2 karakter");
    try {
      await api.put(`/admin/categories/${c.id}`, { name: editName.trim() });
      toast.success(`Kategori diubah menjadi "${editName.trim()}"`);
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah nama"); }
  };

  const doMerge = async (src) => {
    if (!mergeTarget) return toast.error("Pilih kategori tujuan dulu");
    const tgt = cats.find((c) => c.id === mergeTarget);
    if (!window.confirm(`Gabungkan "${src.name}" (${src.product_count} produk) ke "${tgt?.name}"? Kategori "${src.name}" akan dihapus.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/categories/merge", { source_id: src.id, target_id: mergeTarget });
      toast.success(`${data.moved} produk dipindah ke "${tgt?.name}" — kategori "${src.name}" dihapus`);
      setMerging(null); setMergeTarget("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menggabungkan"); }
    finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Hapus kategori "${c.name}"?`)) return;
    try {
      await api.delete(`/admin/categories/${c.id}`);
      toast.success("Kategori dihapus");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menghapus"); }
  };

  if (cats === null) return <div className="py-20 text-center text-xs text-slate-400">Memuat...</div>;
  if (cats === false) return <div className="py-20 text-center text-xs text-slate-400">Halaman ini khusus admin.</div>;

  return (
    <div data-testid="categories-page" className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Kategori Produk</h1>
          <p className="text-xs text-slate-400">{cats.length} kategori • {usedCount} terpakai produk • {cats.length - usedCount} kosong</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 text-slate-300 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="cat-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari kategori..." className={`${inp} w-full pl-8`} />
        </div>
        <input data-testid="cat-new-name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Kategori baru..." className={`${inp} w-48`} />
        <button data-testid="cat-add-btn" onClick={add} disabled={busy} className="h-9 px-4 rounded-lg bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50">
          <Plus className="w-3.5 h-3.5" /> Tambah
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50/60 border-b border-slate-100">
              <th className="text-left px-4 py-2.5 font-semibold">Nama Kategori</th>
              <th className="text-left px-3 py-2.5 font-semibold">ID</th>
              <th className="text-center px-3 py-2.5 font-semibold w-24">Produk</th>
              <th className="px-3 py-2.5 w-56"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {pagedCats.map((c) => (
              <tr key={c.id} data-testid={`cat-row-${c.id}`}>
                <td className="px-4 py-2">
                  {editing === c.id ? (
                    <div className="flex items-center gap-1.5">
                      <input data-testid={`cat-edit-input-${c.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && rename(c)} autoFocus className={`${inp} w-52`} />
                      <button data-testid={`cat-edit-save-${c.id}`} onClick={() => rename(c)} className="w-8 h-8 rounded-lg bg-[#C4738A] text-white flex items-center justify-center hover:bg-[#b3647b]"><Check className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setEditing(null)} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-400 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <span className="text-xs font-semibold text-slate-800">{c.name}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-[10px] font-mono text-slate-400">{c.id}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${c.product_count > 0 ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{c.product_count}</span>
                </td>
                <td className="px-3 py-2">
                  {merging === c.id ? (
                    <div className="flex items-center gap-1.5 justify-end">
                      <select data-testid={`cat-merge-target-${c.id}`} value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)} className={`${inp} w-44`}>
                        <option value="">Pilih tujuan...</option>
                        {cats.filter((x) => x.id !== c.id).map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                      </select>
                      <button data-testid={`cat-merge-confirm-${c.id}`} onClick={() => doMerge(c)} disabled={busy} className="h-8 px-3 rounded-lg bg-slate-800 text-white text-[10px] font-semibold hover:bg-slate-900 disabled:opacity-50">Gabungkan</button>
                      <button onClick={() => { setMerging(null); setMergeTarget(""); }} className="w-8 h-8 rounded-lg border border-slate-200 text-slate-400 flex items-center justify-center"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 justify-end">
                      <button data-testid={`cat-rename-${c.id}`} onClick={() => { setEditing(c.id); setEditName(c.name); }} title="Ubah nama"
                        className="w-8 h-8 rounded-lg text-slate-400 hover:text-[#C4738A] hover:bg-rose-50 flex items-center justify-center transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`cat-merge-${c.id}`} onClick={() => { setMerging(c.id); setMergeTarget(""); }} title="Gabung ke kategori lain"
                        className="w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center transition-colors"><Merge className="w-3.5 h-3.5" /></button>
                      {c.product_count === 0 && (
                        <button data-testid={`cat-delete-${c.id}`} onClick={() => del(c)} title="Hapus kategori kosong"
                          className="w-8 h-8 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {!filtered.length && <tr><td colSpan={4} className="px-4 py-10 text-center text-xs text-slate-400">Tidak ada kategori yang cocok.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager {...catPager} testid="cat-pager" />
      <p className="text-[10px] text-slate-400 flex items-center gap-1.5"><Layers className="w-3 h-3" /> Kategori yang masih dipakai produk hanya bisa digabung, tidak bisa dihapus — produk otomatis ikut pindah.</p>
    </div>
  );
}
